import * as THREE from 'three';
import { inPanoWorker } from '../pano.js';
import {
  lightHalf,
  skyParts,
  type FloatImage,
  type HalfImage,
  type SkyAsk,
  type SkyParts,
} from './sky-parts.js';

/**
 * A world's sky, from its HDRI (ADR-032). The float image is used twice and
 * then let go, both in the pano worker (look/sky-parts.ts): baked to 8-bit
 * for the background -- tone-mapped there once, so it costs a quarter of the
 * float one and is not tone-mapped again -- and made into the background's
 * cube faces, as a 360° photo's are; and halved to 1024 × 512 for the
 * environment light, whose PMREM at 2k would be four times the memory for
 * light nobody can tell apart. Here the parts become textures.
 *
 * The background is the faces as a cube a third of the sky's width on a side
 * (render/pano-faces.ts: 688 px for a 2k sky, 7.6 px a degree at a face's
 * centre against the sky's 5.7), at most the GPU's `maxCubemapSize`, made
 * with render/pano.ts's `cubeTexture`. Given the 2D image instead, three
 * would make a cube the sky's height on a side with six depth buffers and
 * keep the image beside it: measured at 2k, 58.7 MB against these 11.4 (the
 * 30 Sep 2026 correction of worlds/budget.ts).
 */

export {
  bakeSky,
  displayed,
  halve,
  type FloatImage,
  type HalfImage,
  type SkyAsk,
  type SkyParts,
} from './sky-parts.js';

/** The environment light's source from the worker's half floats, as they are. */
export function lightTexture(light: HalfImage): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    light.data,
    light.width,
    light.height,
    THREE.RGBAFormat,
    THREE.HalfFloatType,
  );
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.LinearSRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  // Rows top first, as HDRLoader leaves them and as the import measured the sun.
  texture.flipY = true;
  texture.needsUpdate = true;
  return texture;
}

/** The environment light's source: at most 1024 wide, half floats (linearly filterable everywhere). */
export function lightSource(image: FloatImage, maxWidth = 1024): THREE.DataTexture {
  return lightTexture(lightHalf(image, maxWidth));
}

/**
 * The sky's parts made in the pano worker, the float image handed over
 * rather than copied (the page has no more use for it); on the page in a
 * browser without workers, and in Node. About 230 ms of a desktop's main
 * thread before (bake and faces), a second of a Quest's: a world's load no
 * longer hitches for it.
 */
export async function skyInWorker(ask: SkyAsk): Promise<SkyParts> {
  if (typeof Worker !== 'function') return skyParts(ask);
  const { width, height } = ask.image;
  const answer = await inPanoWorker({ sky: ask }, [ask.image.data.buffer as ArrayBuffer]);
  if (!('sky' in answer)) throw new Error('pano worker answered no sky');
  const { sky } = answer;
  console.info(
    `world sky ${width} × ${height}: ` +
      (sky.faces ? `faces ${sky.faces.size}² in ${Math.round(sky.ms.bake)} ms baking + ` : '') +
      (sky.faces ? `${Math.round(sky.ms.faces)} ms sampling + ` : '') +
      `${Math.round(sky.ms.light)} ms for the light, in the pano worker`,
  );
  return sky;
}

/**
 * A sky when the HDRI could not be had: the manifest's horizon and zenith
 * colours as a vertical gradient, small, so fog and horizon still meet.
 */
export function gradientSky(horizon: string, zenith: string): THREE.DataTexture {
  const width = 8;
  const height = 64;
  const data = new Uint8Array(width * height * 4);
  // Mixed as the sRGB bytes the manifest gives, which is how they are displayed.
  for (let y = 0; y < height; y++) {
    const up = 1 - (y + 0.5) / height;
    const t = THREE.MathUtils.smoothstep(up, 0.5, 0.9);
    const rgb = [0, 1, 2].map((c) => {
      const a = parseInt(horizon.slice(1 + c * 2, 3 + c * 2), 16);
      const b = parseInt(zenith.slice(1 + c * 2, 3 + c * 2), 16);
      return Math.round(a + (b - a) * t);
    });
    for (let x = 0; x < width; x++) data.set([rgb[0]!, rgb[1]!, rgb[2]!, 255], (y * width + x) * 4);
  }
  const texture = new THREE.DataTexture(data, width, height);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.flipY = true;
  texture.needsUpdate = true;
  return texture;
}
